import React, { useEffect, useMemo, useState } from "react";
import {
  FiCheck,
  FiChevronDown,
  FiEdit2,
  FiKey,
  FiRefreshCw,
  FiSearch,
  FiShield,
  FiTrash2,
  FiUser,
  FiX,
} from "react-icons/fi";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import StorefrontIcon from "@mui/icons-material/Storefront";
import GroupsIcon from "@mui/icons-material/Groups";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import BadgeIcon from "@mui/icons-material/Badge";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { AUTH_API_BASE } from "./config/api";

const moduleCatalog = [
  {
    key: "salesbyitems",
    label: "Sales By Customer",
    group: "Sales",
    icon: <PointOfSaleIcon fontSize="small" />,
  },
  {
    key: "salesitems1",
    label: "Sales By Items",
    group: "Sales",
    icon: <Inventory2Icon fontSize="small" />,
  },
  {
    key: "purchaseitems",
    label: "Purchase By Items",
    group: "Purchases",
    icon: <ShoppingCartIcon fontSize="small" />,
  },
  {
    key: "purchasebycustomer",
    label: "Purchase By Supplier",
    group: "Purchases",
    icon: <StorefrontIcon fontSize="small" />,
  },
  {
    key: "purchaserreport",
    label: "Purchaser Report",
    group: "Reports",
    icon: <GroupsIcon fontSize="small" />,
  },
  {
    key: "monthlybudget1",
    label: "Monthly Budget",
    group: "Planning",
    icon: <CalendarMonthIcon fontSize="small" />,
  },
  {
    key: "stockentry",
    label: "Stock Entry",
    group: "Inventory",
    icon: <WarehouseIcon fontSize="small" />,
  },
  {
    key: "employeereport",
    label: "Employee Report",
    group: "HR",
    icon: <BadgeIcon fontSize="small" />,
  },
  {
    key: "useraccesscontrol",
    label: "User Access Control",
    group: "Admin",
    icon: <AdminPanelSettingsIcon fontSize="small" />,
  },
  {
    key: "itemtable",
    label: "Items Entry",
    group: "Inventory",
    icon: <Inventory2Icon fontSize="small" />,
  },
  {
    key: "itemtableviewer",
    label: "Items Viewer",
    group: "Inventory",
    icon: <VisibilityIcon fontSize="small" />,
  },
];

const roleOptions = [
  { value: "admin", label: "Admin" },
  { value: "limited", label: "Limited" },
  { value: "purchaser", label: "Purchaser" },
  { value: "Transfer", label: "Transfer" },
  { value: "employeeform", label: "Employee Form" },
  { value: "itemadmin", label: "Item Admin" },
  { value: "pdfviewer", label: "PDF Viewer" },
];

const emptyForm = {
  username: "",
  password: "",
  role: "limited",
  access: [],
};

const normalizeAccess = (access) => {
  if (Array.isArray(access)) {
    return access.map((item) => String(item).toLowerCase().trim());
  }

  if (typeof access === "string") {
    try {
      const parsed = JSON.parse(access);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).toLowerCase().trim());
      }
    } catch {
      return access
        .split(",")
        .map((item) => item.toLowerCase().trim())
        .filter(Boolean);
    }
  }

  if (access && typeof access === "object") {
    return Object.keys(access)
      .filter((key) => access[key])
      .map((key) => key.toLowerCase().trim());
  }

  return [];
};

const getModule = (key) => moduleCatalog.find((item) => item.key === key);
const getUserId = (user) => user?.id ?? user?.Id ?? user?.userId ?? user?.UserId ?? null;

export default function UserAccessControl({ currentUser }) {
  const API_BASE = AUTH_API_BASE;

  const [users, setUsers] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [moduleSearch, setModuleSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const isAdmin = currentUser?.role === "admin";

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_BASE}/rolemaster`);
      const data = await res.json();
      const normalized = Array.isArray(data)
        ? data.map((user) => ({
            ...user,
            access: normalizeAccess(user.access),
          }))
        : [];
      setUsers(normalized);
    } catch (err) {
      console.error(err);
    }
  };

  const resetForm = (clearStatus = true) => {
    setForm(emptyForm);
    setEditingId(null);
    setModuleSearch("");
    setIsPickerOpen(false);
    if (clearStatus) setStatus("");
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleAccess = (key) => {
    setForm((prev) => ({
      ...prev,
      access: prev.access.includes(key)
        ? prev.access.filter((item) => item !== key)
        : [...prev.access, key],
    }));
  };

  const handleEdit = (user) => {
    const resolvedId = getUserId(user);
    if (!resolvedId) {
      setStatus("User id not found.");
      return;
    }

    setEditingId(resolvedId);
    setForm({
      username: user.username || "",
      password: user.password || "",
      role: user.role || "limited",
      access: normalizeAccess(user.access),
    });
    setStatus("");
  };

  const handleDelete = async (id) => {
    if (!id) {
      setStatus("User id not found.");
      return;
    }

    if (!window.confirm("Delete this user?")) return;

    try {
      await fetch(`${API_BASE}/rolemaster/${id}`, {
        method: "DELETE",
      });
      setStatus("User deleted.");
      fetchUsers();
    } catch (err) {
      console.error(err);
      setStatus("Delete failed.");
    }
  };

  const handleSubmit = async () => {
    const username = form.username.trim();
    const password = form.password.trim();

    if (!username) {
      setStatus("Username is required.");
      return;
    }

    if (!form.role) {
      setStatus("Select a role.");
      return;
    }

    if (!editingId && !password) {
      setStatus("Password is required.");
      return;
    }

    if (form.role === "limited" && form.access.length === 0) {
      setStatus("Select at least one form.");
      return;
    }

    const normalizedAccess = form.access
      .map((item) => String(item).toLowerCase().trim())
      .filter(Boolean);

    const payload = {
      username,
      password,
      role: form.role,
      access: normalizedAccess.join(","),
      accessList: normalizedAccess,
    };

    const method = editingId ? "PUT" : "POST";
    const url = editingId
      ? `${API_BASE}/rolemaster/${editingId}`
      : `${API_BASE}/rolemaster`;

    try {
      setIsSaving(true);
      setStatus("");

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error("Save failed");
      }

      resetForm(false);
      setStatus(editingId ? "User updated." : "User created.");
      fetchUsers();
    } catch (err) {
      console.error(err);
      setStatus("Save failed.");
    } finally {
      setIsSaving(false);
    }
  };

  const filteredModules = useMemo(() => {
    const term = moduleSearch.toLowerCase().trim();
    if (!term) return moduleCatalog;

    return moduleCatalog.filter((item) => {
      return (
        item.label.toLowerCase().includes(term) ||
        item.group.toLowerCase().includes(term)
      );
    });
  }, [moduleSearch]);

  const filteredUsers = useMemo(() => {
    const term = search.toLowerCase().trim();
    if (!term) return users;

    return users.filter((user) => {
      const username = String(user.username || "").toLowerCase();
      const role = String(user.role || "").toLowerCase();
      const access = normalizeAccess(user.access).join(" ");
      return username.includes(term) || role.includes(term) || access.includes(term);
    });
  }, [search, users]);

  const selectedModules = useMemo(() => {
    return form.access.map(getModule).filter(Boolean);
  }, [form.access]);

  if (!isAdmin) {
    return <div style={{ padding: 40 }}>No permission</div>;
  }

  return (
    <div style={styles.page}>
      <div style={styles.canvasGlowA} />
      <div style={styles.canvasGlowB} />

      <div style={styles.headerBar}>
        <div>
          <h1 style={styles.pageTitle}>User Access Control</h1>
          <div style={styles.pageSubline}>Portal user login and permission management</div>
        </div>
      </div>

      <div style={styles.shell}>
        <section style={styles.formPanel}>
          <div style={styles.panelTop}>
            <div>
              <div style={styles.panelEyebrow}>Account Setup</div>
              <h2 style={styles.panelTitle}>{editingId ? "Update User" : "Create User"}</h2>
            </div>

            {editingId && (
              <button type="button" style={styles.clearButton} onClick={() => resetForm()}>
                <FiRefreshCw />
                Reset
              </button>
              )}
            </div>

          <div style={styles.formBody}>
            <div style={styles.formSection}>
              <div style={styles.inputGrid}>
                <label style={styles.fieldCard}>
                  <span style={styles.fieldLabel}>
                    <FiUser />
                    Username
                  </span>
                  <input
                    style={styles.input}
                    value={form.username}
                    onChange={(e) => handleChange("username", e.target.value)}
                    placeholder="Username"
                  />
                </label>

                <label style={styles.fieldCard}>
                  <span style={styles.fieldLabel}>
                    <FiKey />
                    Password
                  </span>
                  <input
                    type="password"
                    style={styles.input}
                    value={form.password}
                    onChange={(e) => handleChange("password", e.target.value)}
                    placeholder="Password"
                  />
                </label>
              </div>

              <label style={styles.fieldCard}>
                <span style={styles.fieldLabel}>
                  <FiShield />
                  Role
                </span>
                <select
                  style={styles.input}
                  value={form.role}
                  onChange={(e) => handleChange("role", e.target.value)}
                >
                  {roleOptions.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div style={{ ...styles.inlineAccessCard, ...styles.formSection }}>
              <div style={styles.inlineAccessTop}>
                <span style={styles.fieldLabel}>
                  <FiShield />
                  Form Permissions
                </span>
                <div style={styles.selectedCount}>{form.access.length} Selected</div>
              </div>

              <div style={styles.permissionToolbar}>
                <div style={styles.searchField}>
                  <FiSearch style={styles.searchIcon} />
                  <input
                    style={styles.searchInput}
                    value={moduleSearch}
                    onChange={(e) => setModuleSearch(e.target.value)}
                    placeholder="Search form"
                  />
                </div>

                <button
                  type="button"
                  style={styles.dropdownToggle}
                  onClick={() => setIsPickerOpen((prev) => !prev)}
                >
                  <span>{isPickerOpen ? "Hide Forms" : "Open Forms"}</span>
                  <FiChevronDown
                    style={{
                      transform: isPickerOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </button>
              </div>

              {isPickerOpen && (
                <div style={styles.permissionDropdown}>
                  {filteredModules.length > 0 ? (
                    filteredModules.map((item) => {
                      const active = form.access.includes(item.key);
                      return (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => toggleAccess(item.key)}
                          style={{
                            ...styles.permissionOption,
                            ...(active ? styles.permissionOptionActive : {}),
                          }}
                        >
                          <span style={styles.permissionOptionIcon}>{item.icon}</span>
                          <span style={styles.permissionOptionText}>
                            <strong>{item.label}</strong>
                            <small>{item.group}</small>
                          </span>
                          {active && <FiCheck style={styles.permissionCheck} />}
                        </button>
                      );
                    })
                  ) : (
                    <div style={styles.emptyBox}>No forms found.</div>
                  )}
                </div>
              )}

              <div style={styles.selectedArea}>
                {selectedModules.length > 0 ? (
                  selectedModules.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => toggleAccess(item.key)}
                      style={styles.selectedToken}
                    >
                      <span style={styles.selectedTokenIcon}>{item.icon}</span>
                      <span>{item.label}</span>
                      <FiX />
                    </button>
                  ))
                ) : (
                  <div style={styles.emptyText}>No forms selected</div>
                )}
              </div>
            </div>

            {status ? <div style={styles.statusBar}>{status}</div> : null}
          </div>

          <div style={styles.footerActions}>
            <button type="button" style={styles.primaryButton} onClick={handleSubmit} disabled={isSaving}>
              {isSaving ? "Saving..." : editingId ? "Update User" : "Create User"}
            </button>
            <button type="button" style={styles.secondaryButton} onClick={() => resetForm()}>
              Clear
            </button>
          </div>
        </section>

        <section style={styles.tablePanel}>
          <div style={styles.tableTop}>
            <div>
              <div style={styles.panelEyebrow}>User Directory</div>
              <h2 style={styles.panelTitle}>Users</h2>
            </div>

            <div style={styles.tableSearch}>
              <FiSearch style={styles.searchIcon} />
              <input
                style={styles.searchInput}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search user"
              />
            </div>
          </div>

          <div style={styles.tableCard}>
            <div style={styles.tableHead}>
              <div style={styles.headCell}>Username</div>
              <div style={styles.headCell}>Password</div>
              <div style={styles.headCell}>Role</div>
              <div style={styles.headCell}>Form Permission</div>
              <div style={styles.headCell}>Actions</div>
            </div>

            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <div key={getUserId(user) || user.username} style={styles.tableRow}>
                  <div style={styles.bodyCell}>
                    <div style={styles.userName}>{user.username}</div>
                  </div>

                  <div style={{ ...styles.bodyCell, ...styles.passwordCell }}>
                    {user.password || "-"}
                  </div>

                  <div style={{ ...styles.bodyCell, ...styles.roleCell }}>
                    <span style={styles.roleBadge}>{String(user.role || "").toUpperCase()}</span>
                  </div>

                  <div style={styles.bodyCell}>
                    <div style={styles.permissionChips}>
                      {normalizeAccess(user.access).length > 0 ? (
                        normalizeAccess(user.access).map((key) => {
                          const item = getModule(key);
                          return (
                            <span key={key} style={styles.permissionChip}>
                              <span style={styles.permissionChipIcon}>{item?.icon}</span>
                              {item?.label || key}
                            </span>
                          );
                        })
                      ) : (
                        <span style={styles.emptyText}>No access</span>
                      )}
                    </div>
                  </div>

                  <div style={{ ...styles.bodyCell, ...styles.actionCell }}>
                    <div style={styles.actionButtons}>
                      <button type="button" style={styles.editButton} onClick={() => handleEdit(user)}>
                        <FiEdit2 />
                        Update
                      </button>
                      <button
                        type="button"
                        style={styles.deleteButton}
                        onClick={() => handleDelete(getUserId(user))}
                      >
                        <FiTrash2 />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div style={styles.noRows}>No users found.</div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

const styles = {
  page: {
    position: "relative",
    minHeight: "auto",
    padding: "12px 18px 18px",
    background: "#eef3f7",
    fontFamily: "'Calibri', 'Arial Nova', 'Segoe UI', sans-serif",
    overflow: "visible",
  },
  canvasGlowA: {
    position: "absolute",
    top: -120,
    left: -80,
    width: 280,
    height: 280,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(15,118,110,0.16), transparent 70%)",
    pointerEvents: "none",
  },
  canvasGlowB: {
    position: "absolute",
    right: -100,
    top: 80,
    width: 320,
    height: 320,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(249,115,22,0.10), transparent 72%)",
    pointerEvents: "none",
  },
  headerBar: {
    position: "relative",
    zIndex: 1,
    marginBottom: 10,
  },
  pageTitle: {
    margin: 0,
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: "-0.03em",
    color: "#102033",
    fontFamily: "'Bahnschrift', 'Arial Narrow', 'Segoe UI', sans-serif",
  },
  pageSubline: {
    marginTop: 2,
    fontSize: 11,
    color: "#607086",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    fontFamily: "'Bahnschrift', 'Arial Narrow', 'Segoe UI', sans-serif",
  },
  shell: {
    position: "relative",
    zIndex: 1,
    display: "grid",
    gridTemplateColumns: "minmax(270px, 0.62fr) minmax(790px, 1.58fr)",
    gap: 12,
    alignItems: "stretch",
  },
  formPanel: {
    background: "rgba(255,255,255,0.94)",
    border: "1px solid rgba(148,163,184,0.16)",
    borderRadius: 28,
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    boxShadow: "0 24px 60px rgba(15,23,42,0.08)",
    backdropFilter: "blur(14px)",
    minHeight: "auto",
  },
  tablePanel: {
    background: "rgba(255,255,255,0.94)",
    border: "1px solid rgba(148,163,184,0.16)",
    borderRadius: 28,
    padding: 12,
    boxShadow: "0 24px 60px rgba(15,23,42,0.08)",
    backdropFilter: "blur(14px)",
    minWidth: 0,
    minHeight: "auto",
  },
  panelTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "start",
    gap: 14,
    flexWrap: "wrap",
  },
  formBody: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    flex: 1,
  },
  formSection: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  panelEyebrow: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    color: "#738398",
    fontWeight: 800,
    marginBottom: 6,
    fontFamily: "'Bahnschrift', 'Arial Narrow', 'Segoe UI', sans-serif",
  },
  panelTitle: {
    margin: 0,
    fontSize: 18,
    fontWeight: 800,
    color: "#102033",
    fontFamily: "'Bahnschrift', 'Arial Narrow', 'Segoe UI', sans-serif",
  },
  clearButton: {
    border: "1px solid #d7e0ea",
    background: "#fff",
    color: "#304256",
    borderRadius: 14,
    padding: "8px 12px",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 700,
    cursor: "pointer",
  },
  inputGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
    marginBottom: 10,
  },
  fieldCard: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: 12,
    borderRadius: 18,
    background: "#f8fbfd",
    border: "1px solid #e0e8ef",
  },
  fieldLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    fontWeight: 700,
    color: "#415468",
  },
  input: {
    border: "1px solid #d2dbe5",
    background: "#fff",
    borderRadius: 14,
    padding: "12px 14px",
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
    color: "#102033",
  },
  inlineAccessCard: {
    borderRadius: 24,
    background: "linear-gradient(180deg, #f9fbfd 0%, #f2f7fb 100%)",
    border: "1px solid #dfe8f0",
    padding: 14,
  },
  inlineAccessTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  selectedCount: {
    padding: "6px 10px",
    borderRadius: 999,
    background: "#102033",
    color: "#fff",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  permissionToolbar: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 10,
    marginBottom: 12,
  },
  searchField: {
    position: "relative",
  },
  searchIcon: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#738398",
  },
  searchInput: {
    width: "100%",
    border: "1px solid #d2dbe5",
    background: "#fff",
    borderRadius: 14,
    padding: "12px 14px 12px 38px",
    fontSize: 13,
    outline: "none",
    boxSizing: "border-box",
    color: "#102033",
  },
  dropdownToggle: {
    border: "1px solid #d2dbe5",
    background: "#fff",
    borderRadius: 14,
    padding: "12px 14px",
    minWidth: 128,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    cursor: "pointer",
    fontWeight: 700,
    color: "#102033",
  },
  permissionDropdown: {
    border: "1px solid #dfe8f0",
    background: "#fff",
    borderRadius: 18,
    overflow: "hidden",
    maxHeight: 310,
    overflowY: "auto",
    boxShadow: "0 18px 36px rgba(15,23,42,0.08)",
    marginBottom: 14,
  },
  permissionOption: {
    width: "100%",
    border: "none",
    borderBottom: "1px solid #edf2f7",
    background: "#fff",
    padding: "12px 14px",
    display: "flex",
    alignItems: "center",
    gap: 12,
    textAlign: "left",
    cursor: "pointer",
  },
  permissionOptionActive: {
    background: "#ebf8f4",
  },
  permissionOptionIcon: {
    width: 38,
    height: 38,
    borderRadius: 12,
    background: "#f2f6fa",
    color: "#0f766e",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  permissionOptionText: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    color: "#102033",
  },
  permissionCheck: {
    marginLeft: "auto",
    color: "#0f766e",
    fontSize: 18,
  },
  selectedArea: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    minHeight: 36,
  },
  selectedToken: {
    border: "1px solid #cfe3d9",
    background: "#ffffff",
    color: "#114b3a",
    borderRadius: 999,
    padding: "8px 11px",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontWeight: 700,
    cursor: "pointer",
  },
  selectedTokenIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#0f766e",
  },
  emptyText: {
    color: "#7b8ca0",
    fontSize: 13,
    alignSelf: "center",
  },
  emptyBox: {
    padding: 16,
    color: "#7b8ca0",
    textAlign: "center",
  },
  statusBar: {
    marginTop: 12,
    padding: "12px 14px",
    borderRadius: 14,
    background: "#f6f8fb",
    border: "1px solid #d9e2eb",
    color: "#2c4259",
    fontWeight: 700,
  },
  footerActions: {
    display: "flex",
    gap: 12,
    marginTop: "auto",
    flexWrap: "wrap",
    paddingTop: 4,
  },
  primaryButton: {
    border: "none",
    background: "linear-gradient(135deg, #0f766e 0%, #115e59 100%)",
    color: "#fff",
    borderRadius: 16,
    padding: "11px 16px",
    fontWeight: 800,
    cursor: "pointer",
    minWidth: 128,
    boxShadow: "0 14px 28px rgba(15,118,110,0.18)",
  },
  secondaryButton: {
    border: "1px solid #d7e0ea",
    background: "#fff",
    color: "#304256",
    borderRadius: 16,
    padding: "11px 16px",
    fontWeight: 800,
    cursor: "pointer",
  },
  tableTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "start",
    marginBottom: 12,
    flexWrap: "wrap",
  },
  tableSearch: {
    position: "relative",
    minWidth: 240,
  },
  tableCard: {
    borderRadius: 22,
    overflow: "hidden",
    border: "1px solid #dfe8f0",
    background: "#fff",
    maxHeight: "72vh",
    overflowX: "auto",
    overflowY: "auto",
  },
  tableHead: {
    display: "grid",
    gridTemplateColumns:
      "minmax(132px, 1fr) minmax(72px, 0.42fr) minmax(145px, 0.9fr) minmax(220px, 1.55fr) minmax(156px, 0.88fr)",
    background: "#f5f8fb",
    position: "sticky",
    top: 0,
    zIndex: 2,
    borderBottom: "1px solid #e5edf4",
    minWidth: 725,
  },
  headCell: {
    padding: "11px 12px",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#607086",
    borderRight: "1px solid #edf2f7",
  },
  tableRow: {
    display: "grid",
    gridTemplateColumns:
      "minmax(132px, 1fr) minmax(72px, 0.42fr) minmax(145px, 0.9fr) minmax(220px, 1.55fr) minmax(156px, 0.88fr)",
    borderBottom: "1px solid #edf2f7",
    alignItems: "start",
    minWidth: 725,
  },
  bodyCell: {
    padding: "10px 12px",
    borderRight: "1px solid #f1f5f9",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    minHeight: 60,
    color: "#102033",
    fontSize: 13,
    lineHeight: 1.4,
    overflowWrap: "break-word",
  },
  userName: {
    fontWeight: 700,
    color: "#102033",
  },
  passwordCell: {
    fontFamily: "'Consolas', 'Courier New', monospace",
    letterSpacing: "0.02em",
    fontSize: 12,
  },
  roleCell: {
    alignItems: "center",
    whiteSpace: "nowrap",
  },
  roleBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "7px 12px",
    borderRadius: 999,
    background: "#e9f7f4",
    color: "#0f766e",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    minWidth: 118,
  },
  permissionChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    alignItems: "flex-start",
  },
  permissionChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 8px",
    borderRadius: 999,
    background: "#f3f7fb",
    color: "#28435e",
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1.2,
  },
  permissionChipIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#0f766e",
  },
  actionButtons: {
    display: "flex",
    gap: 6,
    flexWrap: "nowrap",
    whiteSpace: "nowrap",
    alignItems: "center",
  },
  actionCell: {
    alignItems: "center",
    whiteSpace: "nowrap",
  },
  editButton: {
    border: "1px solid #d7e0ea",
    background: "#fff",
    color: "#2563eb",
    borderRadius: 12,
    padding: "7px 8px",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 10,
    fontWeight: 700,
    cursor: "pointer",
  },
  deleteButton: {
    border: "1px solid #fecaca",
    background: "#fff6f6",
    color: "#dc2626",
    borderRadius: 12,
    padding: "7px 8px",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 10,
    fontWeight: 700,
    cursor: "pointer",
  },
  noRows: {
    padding: 24,
    textAlign: "center",
    color: "#7b8ca0",
    fontWeight: 600,
  },
};
